import { DataTable } from "@/components";
import { Button } from "@/components/ui";
import { LOGIN_ROLES, toYMD } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { PdcData } from "@/services";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ColumnDef } from "@tanstack/react-table";
import { Check, InfoIcon, Plus } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { UpdatePdc } from ".";

interface PdcDetailsTableProps {
  data: PdcData[];
  isLoading?: boolean;
  onAddNew?: () => void;
  onPdcCheck?: (checked: boolean) => void;
  isPdcCheckEnabled?: boolean;
  installmentId: string;
  onPdcUpdate?: () => void;
}

const PdcDetailsTable: FC<PdcDetailsTableProps> = ({
  data,
  isLoading = false,
  onAddNew,
  onPdcCheck,
  isPdcCheckEnabled = false,
  installmentId,
  onPdcUpdate,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPdc, setEditingPdc] = useState<PdcData | null>(null);

  const handleAddNew = () => {
    setEditingPdc(null);
    setIsFormOpen(true);
  };

  const handleEdit = (pdc: PdcData) => {
    setEditingPdc(pdc);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingPdc(null);
  };

  const handleFormSuccess = () => {
    onPdcUpdate?.();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

    const loggedInUserData = useSelector(
      (state: RootState) => state.currentData.loggedInUserData
    );
  
    // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
    const roles = loggedInUserData?.role || [];
  
    // Helper to check if user has a given role
    const hasRole = (role: string) => Array.isArray(roles) && roles.includes(role);

    
  const columns: ColumnDef<PdcData>[] = useMemo(
    () => [
      {
        accessorKey: "pdcNumber",
        header: "PDC #",
        cell: (row: any) => (
          <span
            className="text-primary font-normal hover:cursor-pointer"
            onClick={() => {
              handleEdit(row?.row?.original);
            }}
          >
            {String(row?.row?.original?.pdcNumber || "")}
          </span>
        ),
      },
      {
        accessorKey: "year",
        header: "Year",
        cell: ({ getValue }) => (
          <span className="text-gray-800">{getValue() as string}</span>
        ),
      },
      {
        accessorKey: "pdcAmount",
        header: "PDC Amount",
        cell: ({ getValue }) => (
          <span className="text-gray-800">
            {formatCurrency(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: "pdcDate",
        header: "Date",
        cell: ({ getValue }) => (
          <span className="text-gray-800">
            {formatDate(getValue() as string)}
          </span>
        ),
      },
      {
        accessorKey: "pdcStatus",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue() as string;
          return (
            <>
              {status ? (
                <span className={`font-medium text-green-600`}>Received</span>
              ) : (
                <span className={`font-medium text-red-600`}>Not Received</span>
              )}
            </>
          );
        },
      },
      {
        accessorKey: "pdcAmountReceived",
        header: "Amount Received",
        cell: ({ getValue }) => {
          const amount = getValue() as number;
          return (
            <span className="text-gray-800">
              {amount > 0 ? formatCurrency(amount) : "₹ 00"}
            </span>
          );
        },
      },
      {
        accessorKey: "pdcClearedDate",
        header: "Cleared Date",
        cell: ({ getValue }) => {
          const date = getValue() as string;
          return (
            <span className="text-gray-800">
              {date ? formatDate(date) : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "remarksHistory",
        header: "Remarks",
        cell: ({ row }) => {
          const remarksHistory = row.original.remarksHistory;
          if (!remarksHistory || remarksHistory.length === 0) {
            return <span className="text-gray-400">-</span>;
          }

          return (
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <InfoIcon className="w-4 h-4 text-blue-500 cursor-pointer" />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="right"
                    align="center"
                    className="bg-white border rounded shadow-lg p-2 m-2 text-md text-gray-800"
                  >
                    <ul className="space-y-1">
                      {remarksHistory.map((item, idx) => (
                        <li key={idx}>
                          <span className="font-semibold text-dark-blue-600">
                            {idx + 1}. Remark By{" "}
                            {row.original?.pdcCreatedByData?.name} on{" "}
                            {toYMD(item?.createdAt)} -
                          </span>{" "}
                          {item.pdcRemarks}
                        </li>
                      ))}
                    </ul>
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          );
        },
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg text-primary">PDC Details</h2>
          <div className="flex items-center space-x-2">
            {/* <Button type="button" className="bg-primary text-white" disabled>
              <Check className="w-4 h-4 mr-2" />
              PDC Check
            </Button> */}
            <Button type="button" className="bg-primary text-white" disabled>
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          </div>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg text-primary">PDC Details</h2>
        <div className="flex items-center space-x-2">
          <Button
            type="button"
            className={`${
              isPdcCheckEnabled
                ? "bg-primary text-white hover-none"
                : "bg-gray-200 text-gray-600"
            }`}
            // onClick={() => onPdcCheck?.(!isPdcCheckEnabled)}
          >
            <Check className="w-4 h-4 mr-2" />
            PDC Check
          </Button>
          {(hasRole(LOGIN_ROLES.FINANCE_TEAM) || hasRole(LOGIN_ROLES.SUPER_ADMIN)) && (
            <Button
              type="button"
              className="bg-primary text-white"
              onClick={handleAddNew}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          )}
        </div>
      </div>

      <DataTable columns={columns} data={data} />
      <UpdatePdc
        onCancel={handleFormClose}
        open={isFormOpen}
        editData={editingPdc}
      />
    </div>
  );
};

export default PdcDetailsTable;
